import React, { ReactNode, useEffect, useRef, useState } from "react"
import tw from "twin.macro"
import Icon from "../../../atoms/Icon"
import AliceCarousel, { EventObject } from "react-alice-carousel"
import "react-alice-carousel/lib/alice-carousel.css"
import { ModelHighlightsHotspotProps } from "./ModelHighlightsModuleProps"
import { scrollIntoView } from "seamless-scroll-polyfill"
import Hotspot from "./Hotspot"
import useDrag from "../../../../hooks/useDrag"
import { Hotspot as HotspotType } from "../../XseriesModel/XseriesModel.d"

const ModelHighlightsHotspots: React.FC<ModelHighlightsHotspotProps> = ({
  hotspots,
  setActiveSpot,
  activeSpot,
  currentAngle,
  setCurrentAngle,
}) => {
  const isMounted = useRef(false)
  const sectionRef = useRef<HTMLDivElement>(null)
  const isInView = useRef(false)
  const parentXseriesHotspotContainer = useRef<HTMLDivElement | null>(null)

  const getDisclaimerParentPosition = (): {
    left: number | null
    right: number | null
  } => {
    if (window?.innerWidth < 768) return { left: null, right: null }
    if (!parentXseriesHotspotContainer?.current)
      return { left: null, right: null }
    const parentContainerRect =
      parentXseriesHotspotContainer.current?.getBoundingClientRect()

    const parentContainerStyle = getComputedStyle(
      parentXseriesHotspotContainer?.current,
    )
    const paddingLeft =
      Number(parentContainerStyle?.paddingLeft?.replace("px", "")) + 10
    const paddingRight =
      Number(parentContainerStyle?.paddingRight?.replace("px", "")) + 10

    return {
      left: parentContainerRect?.left + paddingLeft,
      right: parentContainerRect?.right - paddingRight,
    }
  }

  useEffect(() => {
    const observer = new IntersectionObserver(
      entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            isInView.current = true
          } else {
            isInView.current = false
          }
        })
      },
      { threshold: 0.1 },
    )

    if (sectionRef.current) {
      observer.observe(sectionRef.current)
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current)
      }
    }
  }, [])

  useEffect(() => {
    if (isMounted.current && isInView.current) {
      if (activeSpot !== null) {
        const element = document.getElementById(`hotspot-${activeSpot?._key}`)
        if (element) {
          scrollIntoView(element, {
            behavior: "smooth",
            inline: "start",
            block: "nearest",
          })
        }
      }
    } else {
      isMounted.current = true
    }
  }, [activeSpot])

  const [items, setItems] = useState<ReactNode[]>([])

  useEffect(() => {
    const disclaimerParentPosition = getDisclaimerParentPosition()
    setItems(
      hotspots?.map((hotspot: HotspotType, i: number) => (
        <div key={i} css={[i == 0 ? tw`ml-0` : tw`ml-6`, tw`w-auto`]}>
          <Hotspot
            hotspot={hotspot}
            activeSpot={activeSpot}
            setActiveSpot={setActiveSpot}
            parentLeft={disclaimerParentPosition.left ?? 0}
            parentRight={disclaimerParentPosition.right ?? 0}
            currentAngle={currentAngle}
            setCurrentAngle={setCurrentAngle}
            imageIndex={i}
          />
        </div>
      )),
    )
  }, [hotspots, activeSpot, currentAngle])

  const updateSliderPosition = (hotspot: { angle: number }) => {
    setActiveSpot(hotspot)
    if (hotspot.angle !== currentAngle) {
      setCurrentAngle(hotspot.angle)
    }
  }
  const responsive = {
    0: { items: 1 },
    600: { items: 2 },
    1024: { items: 3 },
  }
  const renderPrevButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -left-8 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:bg-gray-400 focus-visible:(bg-gray-400)`,
          tw`lg:(-left-6)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="left" color="white" css={[tw`h-4`]} />
      </button>
    )
  }

  const renderNextButton = ({ isDisabled }: { isDisabled?: boolean }) => {
    return (
      <button
        css={[
          tw`absolute -right-8 top-[45%] -translate-y-1/2 z-50 flex justify-center items-center cursor-pointer w-12 h-8 bg-gunmetal rounded-full transition-all hover:(bg-gray-400) focus-visible:(bg-gray-400)`,
          tw`lg:(-right-1)`,
          isDisabled ? tw`hidden` : tw`flex`,
        ]}
      >
        <Icon.Chevron direction="right" color="white" css={[tw`h-4`]} />
      </button>
    )
  }
  const { handleMouseUp } = useDrag()

  return (
    <div ref={sectionRef} onMouseUp={handleMouseUp} css>
      <div css={[tw`h-auto`]}>
        <div css={[tw`flex flex-nowrap w-auto text-center justify-center`]}>
          <AliceCarousel
            name={`hotspot-carousel`}
            key={`hotspot-carousel`}
            mouseTracking
            items={items}
            disableDotsControls
            paddingLeft={0}
            paddingRight={0}
            responsive={responsive}
            controlsStrategy="alternate"
            renderPrevButton={renderPrevButton}
            renderNextButton={renderNextButton}
            onSlideChanged={(e: EventObject) => {
              updateSliderPosition(hotspots[e.item])
            }}
          />
        </div>
      </div>
    </div>
  )
}

export default ModelHighlightsHotspots
